import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import supabase from "../../../supabase";
import { LoaderSpinner } from "../../../components/spinner/loader";
import DataTable from "../../../components/ui/DataTable";
import { Num } from "../../../components/ui";
import { tokens } from "../../../theme";
import { orderObjectStatus, selectableOrderStatus } from "../../../constants/orderStatus";
import { getOrderStatusStyle } from "../../../constants/statusStyles";
import { formatCurrencyManual } from "../../../utils/formatters";
import { formatEmployeeName } from "../../../utils/ordersListQuery";

const orderTotal = (order) =>
  (parseFloat(order.subTotal) || 0) +
  (parseFloat(order.iva105) || 0) +
  (parseFloat(order.iva210) || 0);

/**
 * Producción por operador.
 *
 * Cuenta las órdenes creadas en el período por cada operador, su monto y en qué
 * estado quedaron. Sirve para ver carga de trabajo y cuánto factura cada uno,
 * y para detectar órdenes que quedaron sin cerrar.
 */
const OperatorProduction = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("day").toDate(),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("orders")
        .select(
          "orderId, createdAt, status, subTotal, iva105, iva210, employeeId, clientSignaturePhoto, employee: employeeId(employeeId, name, lastName)"
        )
        .order("orderId", { ascending: false })
        .limit(3000);

      if (range.from) query = query.gte("createdAt", new Date(range.from).toISOString());
      if (range.to) query = query.lte("createdAt", new Date(range.to).toISOString());

      const { data, error: err } = await query;
      if (err) throw new Error(err.message);
      setOrders(data || []);
    } catch (e) {
      setError(e.message || "No se pudo armar el reporte");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const byOperator = new Map();

    orders.forEach((order) => {
      const key = order.employeeId || "sin";
      const current =
        byOperator.get(key) || {
          key,
          operador: order.employee
            ? formatEmployeeName(order.employee)
            : "Sin operador asignado",
          ordenes: 0,
          monto: 0,
          pendientes: 0,
          terminadas: 0,
          facturadas: 0,
          sinFirma: 0,
        };

      current.ordenes += 1;
      current.monto += orderTotal(order);
      if (order.status === orderObjectStatus.pendiente) current.pendientes += 1;
      if (order.status === orderObjectStatus.terminado) current.terminadas += 1;
      if (order.status === orderObjectStatus.facturada) current.facturadas += 1;
      if (!order.clientSignaturePhoto) current.sinFirma += 1;

      byOperator.set(key, current);
    });

    return [...byOperator.values()].sort((a, b) => b.ordenes - a.ordenes);
  }, [orders]);

  const term = search.trim().toLowerCase();
  const visibleRows = term
    ? rows.filter((row) => row.operador.toLowerCase().includes(term))
    : rows;

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          ordenes: acc.ordenes + row.ordenes,
          monto: acc.monto + row.monto,
          pendientes: acc.pendientes + row.pendientes,
          sinFirma: acc.sinFirma + row.sinFirma,
        }),
        { ordenes: 0, monto: 0, pendientes: 0, sinFirma: 0 }
      ),
    [rows]
  );

  const countColumn = (name, label, dotStatus) => ({
    name,
    label,
    options: {
      filter: false,
      sort: true,
      setCellProps: () => ({ style: { width: 116, textAlign: "right" } }),
      setCellHeaderProps: () => ({ style: { width: 116, textAlign: "right" } }),
      customBodyRender: (value) =>
        value > 0 ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontVariantNumeric: "tabular-nums",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {dotStatus && (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: getOrderStatusStyle(dotStatus).dot,
                }}
              />
            )}
            {value}
          </Box>
        ) : (
          <Num sx={{ fontSize: "13px", color: tokens.ink4 }}>0</Num>
        ),
    },
  });

  const columns = [
    {
      name: "operador",
      label: "Operador",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography
            sx={{
              fontSize: "13.5px",
              fontWeight: 500,
              color: value === "Sin operador asignado" ? tokens.brandDark : tokens.ink,
            }}
          >
            {value}
          </Typography>
        ),
      },
    },
    countColumn("ordenes", "Órdenes"),
    countColumn("pendientes", "Pendientes", orderObjectStatus.pendiente),
    countColumn("terminadas", "Terminadas", orderObjectStatus.terminado),
    countColumn("facturadas", "Facturadas", orderObjectStatus.facturada),
    {
      name: "sinFirma",
      label: "Sin firma",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { width: 110, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 110, textAlign: "right" } }),
        customBodyRender: (value) =>
          value > 0 ? (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                borderRadius: 999,
                padding: "2px 9px",
                fontSize: "12px",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                color: "var(--sup-state-danger-ink)",
                backgroundColor: "var(--sup-state-danger-bg)",
              }}
            >
              {value}
            </Box>
          ) : (
            <Num sx={{ fontSize: "13px", color: tokens.ink4 }}>0</Num>
          ),
      },
    },
    {
      name: "monto",
      label: "Monto facturable",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { width: 152, textAlign: "right", whiteSpace: "nowrap" } }),
        setCellHeaderProps: () => ({ style: { width: 152, textAlign: "right" } }),
        customBodyRender: (value) => (
          <Num sx={{ fontSize: "13.5px", fontWeight: 600 }}>{formatCurrencyManual(value)}</Num>
        ),
      },
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        <LoaderSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, paddingY: 6 }}>
        <Typography sx={{ color: tokens.ink2 }}>{error}</Typography>
        <Button variant="outlined" onClick={load}>
          Reintentar
        </Button>
      </Box>
    );
  }

  const summary = [
    { label: "Órdenes del período", value: totals.ordenes },
    { label: "Monto facturable", value: formatCurrencyManual(totals.monto) },
    { label: "Todavía pendientes", value: totals.pendientes },
    { label: "Sin firma del cliente", value: totals.sinFirma, alert: totals.sinFirma > 0 },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "10px",
        }}
      >
        {summary.map((item) => (
          <Box
            key={item.label}
            sx={{
              backgroundColor: tokens.surface,
              border: `1px solid ${tokens.border}`,
              borderRadius: `${tokens.radiusCard}px`,
              padding: "13px 15px",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "11.5px", fontWeight: 500, color: tokens.ink2 }}>
              {item.label}
            </Typography>
            <Num
              sx={{
                fontSize: "19px",
                fontWeight: 600,
                lineHeight: 1.1,
                color: item.alert ? tokens.brandDark : tokens.ink,
              }}
            >
              {item.value}
            </Num>
          </Box>
        ))}
      </Box>

      <DataTable
        data={visibleRows}
        columns={columns}
        heading="Producción por operador"
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar operador…",
        }}
        dateRange={{
          from: range.from,
          to: range.to,
          onChange: (from, to) => setRange({ from, to }),
          label: "Período",
        }}
        resultCount={visibleRows.length}
        totalCount={rows.length}
        emptyMessage="No hay órdenes creadas en el período elegido"
        options={{
          sortOrder: { name: "ordenes", direction: "desc" },
          rowsPerPage: 25,
          downloadOptions: { filename: "produccion-por-operador.csv", separator: ";" },
        }}
      />

      <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, paddingX: "2px" }}>
        El monto suma subtotal más IVA de las órdenes creadas en el período, sin importar
        si ya se facturaron. Los estados son los que tiene la orden hoy, no los del
        momento en que se creó.
      </Typography>
    </Box>
  );
};

export default OperatorProduction;
